// src/screens/PhoneVerificationScreen.js
import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert, ScrollView, ActivityIndicator, TextInput, Platform } from 'react-native';
import { getAuth, PhoneAuthProvider, RecaptchaVerifier, linkWithCredential, updatePhoneNumber } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useError } from '../components/ErrorContext';
import { db } from '../../firebaseConfig';
import { toE164, phoneSendErrorText, codeSentLine, PHONE_REGION_HINT, PHONE_INVALID_TEXT, PHONE_ALREADY_LINKED_TEXT } from '../utils/phoneNumber';

export default function PhoneVerificationScreen({ navigation, route }) {
  const { showError } = useError();
  const { userData } = route.params || {};
  const auth = getAuth();

  // State for the phone verification flow
  const [isLoading, setLoading] = useState(false);
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  // Most accounts have no phone on file (signup never collects one), so the
  // user types it here. Ownership is proven by the SMS code, not this field.
  // Prefill with the verified number (phoneNumber) so "change my number"
  // from Profile shows what's currently on file.
  const [phoneInput, setPhoneInput] = useState(userData?.phone || userData?.phoneNumber || '');

  // Initialize reCAPTCHA for web. It's essential this is available on the screen.
  useEffect(() => {
    if (Platform.OS === 'web') {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          'size': 'invisible',
          'callback': () => console.log("reCAPTCHA solved")
        });
        window.recaptchaVerifier.render().catch(e => {
          console.error("reCAPTCHA render failed.", e);
          setError("Could not initialize verification. Please go back and try again.");
        });
      }
    }
  }, [auth]);

  // toE164 (E.164 normalization) and the send-error lines are shared with
  // LoginScreen's phone sign-in (src/utils/phoneNumber.js). The error lines
  // never show the raw Firebase message; a blocked SMS region reads as
  // member copy here too (the region policy is project-wide).
  const friendlySendError = (e) => phoneSendErrorText(e, { screen: 'verification' });

  const handleSendVerificationCode = async () => {
    const phone = toE164(phoneInput);
    if (!phone) {
      return setError(PHONE_INVALID_TEXT);
    }
    setError('');
    setLoading(true);
    try {
      const phoneProvider = new PhoneAuthProvider(auth);
      // Use the reCAPTCHA verifier. Ensure the 'recaptcha-container' div exists.
      const verifier = window.recaptchaVerifier;
      const newVerificationId = await phoneProvider.verifyPhoneNumber(phone, verifier);
      setVerificationId(newVerificationId);
      setCodeSent(true); // Update state to show the OTP input
      showError("Code Sent", codeSentLine(phone)); // last 4 digits only (streamers capture their screens)
    } catch (e) {
      setError(friendlySendError(e));
      console.error(e && e.code); // the code only: an AuthError can carry the number
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmVerificationCode = async () => {
    if (!verificationId || !otp) {
      return setError("Please enter the 6-digit code we sent you.");
    }
    setError('');
    setLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      // Attach the phone credential to the signed-in user — this is what
      // actually proves ownership. After it succeeds, the Auth token gains a
      // `phone_number` claim. First-time verify = link; a user who already
      // has a linked phone (changing numbers from Profile) must go through
      // updatePhoneNumber instead — linking twice throws provider-already-linked.
      if (auth.currentUser.phoneNumber) {
        await updatePhoneNumber(auth.currentUser, credential);
      } else {
        await linkWithCredential(auth.currentUser, credential);
      }

      // Refresh the ID token so the new phone_number claim is in scope
      // when the cloud function reads req.auth.token.
      await auth.currentUser.getIdToken(true);

      // Flip the Firestore phoneVerified flag SERVER-SIDE. The cloud
      // function checks req.auth.token.phone_number before writing —
      // a malicious client can't skip linkWithCredential and self-grant.
      // (See firestore.rules: phoneVerified is in the user-doc denylist.)
      const fns = getFunctions();
      const confirmPhone = httpsCallable(fns, 'confirmPhoneVerification');
      await confirmPhone({});

      showError("Success!", "Your phone number has been verified.");
      // Go back to the main verification screen, or fall back to Verification hub if no history
      if (navigation.canGoBack()) navigation.goBack(); else navigation.navigate('Verification');

    } catch (e) {
      if (e.code === 'auth/invalid-verification-code') {
        setError("That code didn't match. Check the 6 digits and try again.");
      } else if (e.code === 'auth/credential-already-in-use' || e.code === 'auth/account-exists-with-different-credential') {
        // A failed phone sign-in can hold the number for up to about 20
        // minutes (the purgePhoneGhosts sweep), so the line names the wait.
        setError(PHONE_ALREADY_LINKED_TEXT);
      } else if (e.code === 'auth/code-expired') {
        setError('That code expired. Go back and request a new one.');
      } else {
        // Member copy only, never the raw message (S2.4).
        console.error('Phone verification confirm error:', e && e.code);
        setError(phoneSendErrorText(e, { screen: 'verification' }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.content}>
        {/* This div is required for the invisible reCAPTCHA to work on web */}
        <View id="recaptcha-container" />

        <Text style={styles.title}>Verify Your Phone Number</Text>
        
        {!codeSent ? (
          <>
            <Text style={styles.subtitle}>
              Enter your mobile number and we'll text you a one-time verification code.
            </Text>
            <TextInput
              style={styles.phoneInput}
              placeholder="(555) 123-4567"
              placeholderTextColor="#666"
              keyboardType="phone-pad"
              autoComplete="tel"
              value={phoneInput}
              onChangeText={setPhoneInput}
            />
            <Text style={styles.hintText}>{PHONE_REGION_HINT}</Text>
            <TouchableOpacity style={styles.submitButton} onPress={handleSendVerificationCode} disabled={isLoading}>
              {isLoading ? <ActivityIndicator color="#000" /> : <Text style={styles.submitButtonText}>Send Code</Text>}
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.subtitle}>Enter the 6-digit code we sent to your phone.</Text>
            <TextInput
              style={styles.input}
              placeholder="6-digit code"
              keyboardType="number-pad"
              value={otp}
              onChangeText={setOtp}
              maxLength={6}
            />
            <TouchableOpacity style={styles.submitButton} onPress={handleConfirmVerificationCode} disabled={isLoading}>
              {isLoading ? <ActivityIndicator color="#000" /> : <Text style={styles.submitButtonText}>Confirm & Verify</Text>}
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { setCodeSent(false); setOtp(''); setVerificationId(null); setError(''); }}>
              <Text style={styles.changeNumberText}>Use a different number</Text>
            </TouchableOpacity>
          </>
        )}

        {error ? <Text style={styles.errorText}>{error}</Text> : null}

        <TouchableOpacity onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))} style={styles.backButton}>
          <Text style={styles.backButtonText}>← Go Back</Text>
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flexGrow: 1, backgroundColor: '#000', justifyContent: 'center', alignItems: 'center' },
  content: { width: '90%', maxWidth: 500, alignItems: 'center' },
  title: { fontSize: 28, fontWeight: 'bold', color: '#FCD000', marginBottom: 10, textAlign: 'center' },
  subtitle: { fontSize: 16, color: '#CCC', textAlign: 'center', marginBottom: 20, paddingHorizontal: 10 },
  phoneInput: { width: '100%', backgroundColor: '#1A1A1A', color: '#fff', padding: 15, borderRadius: 8, fontSize: 20, textAlign: 'center', letterSpacing: 1, marginBottom: 10 },
  hintText: { fontSize: 13, color: '#888', textAlign: 'center', marginBottom: 20 },
  changeNumberText: { color: '#FCD000', fontSize: 15, marginTop: 20, textDecorationLine: 'underline', textAlign: 'center' },
  input: { width: '100%', backgroundColor: '#222', color: '#fff', padding: 15, borderRadius: 8, textAlign: 'center', fontSize: 22, marginBottom: 20, letterSpacing: 5 },
  submitButton: { width: '100%', backgroundColor: '#FCD000', padding: 15, borderRadius: 8, alignItems: 'center' },
  submitButtonText: { fontSize: 18, fontWeight: 'bold', color: '#000' },
  backButton: { marginTop: 30 },
  backButtonText: { color: '#fff', fontSize: 16, textDecorationLine: 'underline' },
  errorText: { color: 'salmon', marginTop: 15, textAlign: 'center' },
});
